import React, { useEffect, useContext, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';

import type { RefetchOptions } from './useAllCommentsData.ts';
import { useAllCommentsData } from './useAllCommentsData.ts';
import { useProcessedCommentsData } from './useProcessedCommentsData.ts';

// Initiates the loading of all comment data for the specified page in the background
// This component is also responsible for processing the raw comments data and updating
// the ProcessedCommentsDataStore with categorized results using default contentStatus filters.

// Experience tracking constants
const COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE = 'comments-data-background-load';

type CommentsDataBackgroundLoaderProps = {
	contentId: string;
};

export const CommentsDataBackgroundLoader = ({
	contentId,
}: CommentsDataBackgroundLoaderProps): React.JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();
	const [
		{ lastProcessedData },
		{
			setProcessedData,
			setError,
			setRefetch,
			clearErrorAndResetIsDataProcessed,
			setDataToProcessed,
			processCommentsData,
		},
	] = useProcessedCommentsData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const previousContentId = useRef<string | undefined>(undefined);

	// Track refetch count to force effect to run after every refetch
	const [refetchCount, setRefetchCount] = useState(0);

	// Call the hook to load all comments - should only when content has settled and is ready so we
	// don't impact loading times
	// Always fetch with network-only to ensure we get fresh data
	const { loading, error, data, refetch } = useAllCommentsData({
		contentId,
		options: {
			skip: !contentId,
			fetchPolicy: 'network-only',
		},
	});

	// Set refetch function in store whenever it changes.
	// Wrap refetch so that when the view calls it (e.g. Retry), we clear error/processed state first.
	// Increment refetchCount in .then() to force the processing effect to run.
	// On refetch failure we always setError, then call the caller's onError if they passed one.
	useEffect(() => {
		if (refetch) {
			const wrappedRefetch = ({ onError: callerOnError }: RefetchOptions = {}) => {
				clearErrorAndResetIsDataProcessed();
				return refetch({
					onError: (err) => {
						setError(err instanceof Error ? err : new Error(String(err)));
						callerOnError?.(err);
					},
				}).then((result: any) => {
					setRefetchCount((c) => c + 1);
					return result;
				});
			};
			setRefetch(wrappedRefetch);
		}
	}, [refetch, setRefetch, clearErrorAndResetIsDataProcessed, setError]);

	// Process and update the ProcessedCommentsDataStore when data changes or refetch completes
	// This is the ONLY place where the processed data store should be updated
	// Depends on refetchCount to ensure effect runs after every refetch
	useEffect(() => {
		// Don't process data while loading
		if (loading) {
			return;
		}

		if (error) {
			setError(error);
			return;
		}

		// When data is undefined (e.g. refetch failed and error was set in the wrapper catch),
		// don't call setProcessedData or we'd overwrite that error with error: undefined.
		if (data === undefined) {
			return;
		}

		// Fast path: if data reference is same as last processed (cached data from refetch),
		// just flip isDataProcessed to true without reprocessing
		if (data === lastProcessedData) {
			setDataToProcessed();
			return;
		}

		const stats = processCommentsData({ data, pageMode });
		if (
			previousContentId.current !== contentId &&
			expValEquals('confluence_fe_emit_comments_data_stats_event', 'isEnabled', true)
		) {
			previousContentId.current = contentId;
			if (stats) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'loaded',
						actionSubject: 'comments',
						actionSubjectId: 'CommentsDataBackgroundLoader',
						objectType: 'page',
						objectId: contentId,
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						attributes: {
							pageMode,
							isLivePage: pageMode === PageMode.LIVE,
							...stats,
						},
					},
				}).fire();
			}
		}
	}, [
		data,
		loading,
		error,
		pageMode,
		setProcessedData,
		setError,
		contentId,
		previousContentId,
		refetchCount,
		lastProcessedData,
		setDataToProcessed,
		processCommentsData,
		createAnalyticsEvent,
	]);

	// Experience tracking: Handle success/failure when loading completes
	useEffect(() => {
		if (!contentId) {
			return;
		}

		// Only track completion if we're not still loading
		if (!loading) {
			if (error) {
				// Experience failed due to error
				experienceTracker.fail({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
					error,
				});
			} else {
				// Experience succeeded - comments data loaded successfully
				experienceTracker.succeed({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
				});
			}
		}
	}, [loading, error, contentId, experienceTracker]);

	// This component only triggers the hook, doesn't render anything other than the ExperienceStart,
	// which handles being rendered multiple times with the same props gracefully
	return (
		<ExperienceStart
			name={COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE}
			attributes={{
				...getCommentsDataManagerExperimentAttributes(),
				contentId,
			}}
		/>
	);
};
